import { BananaGamesLoaderElement } from './element'
import type { BananaGamesLoaderOptions } from './element'

export { BananaGamesLoaderElement }
export type { BananaGamesLoaderOptions }

/** The default custom-element tag name. */
export const BANANA_GAMES_LOADER_TAG = 'banana-games-loader'

/**
 * Register `<banana-games-loader>` (or a custom `tag`). Safe to call many times
 * and inert during SSR (no `customElements`). Importing the package already does
 * this once for the default tag.
 */
export function defineBananaGamesLoader(tag: string = BANANA_GAMES_LOADER_TAG): void {
  if (typeof customElements === 'undefined') return
  if (customElements.get(tag)) return
  customElements.define(tag, BananaGamesLoaderElement)
}

export interface ShowBananaLoaderOptions extends BananaGamesLoaderOptions {
  /** Where to mount the loader. Defaults to `document.body`. */
  target?: Element
}

export interface BananaLoaderHandle {
  /** The mounted element. */
  readonly element: BananaGamesLoaderElement
  /** Resolves when `duration` elapses (the bar fills, the `finished` event fires). */
  readonly finished: Promise<void>
  /** Fade the screen out and remove it. Resolves once gone. */
  hide(): Promise<void>
}

/**
 * Show the loader imperatively — the friendliest API for any framework (or none).
 * Mounts the element, applies options, and hands back a handle to await the intro
 * and to take the screen away when the app is actually ready.
 *
 * Copy is built in and localised from `?lang=` in the URL (then `<html lang>`,
 * then English), so you normally pass nothing but a duration.
 *
 * ```ts
 * const loader = showBananaLoader()          // localised copy, no wiring
 * await appIsReady()   // your own signal, usually later than `loader.finished`
 * await loader.hide()
 * ```
 */
export function showBananaLoader(options: ShowBananaLoaderOptions = {}): BananaLoaderHandle {
  if (typeof document === 'undefined') {
    throw new Error('[banana-loader] showBananaLoader requires a browser (document is undefined).')
  }
  defineBananaGamesLoader()

  const el = document.createElement(BANANA_GAMES_LOADER_TAG) as BananaGamesLoaderElement
  if (options.duration != null) el.duration = options.duration
  if (options.label != null) el.label = options.label
  if (options.doneLabel != null) el.doneLabel = options.doneLabel
  if (options.doneHint != null) el.doneHint = options.doneHint

  const target = options.target ?? document.body
  target.appendChild(el)

  return {
    element: el,
    finished: el.finished,
    hide: () => el.hide(),
  }
}

// Register on import, in browsers. Guarded so it is inert during SSR and
// idempotent if this module is evaluated twice.
defineBananaGamesLoader()
